import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { formatDateByTimezone, formatUnixByTimezone, titleCase } from 'utils/helperFunctions';
import { useGetAssociatedRecords, useGetInteractionLog } from 'apis/business/agent/callLogs/hook';
import {
  useCreateNote,
  useDeleteNote,
  useGetContact,
  useGetNotes,
  useUpdateNote
} from 'apis/business/agent/contact/hook';
import { useGetBusinessProfile } from '@/apis/business/hook';
import InteractionLogForm from '@/components/Common/InteractionLogForm';
import type { FormValues, InteractionLogFormProps } from '@/components/Common/InteractionLogForm';
import { getVisibleJourneyItems } from '@/components/Common/InteractionLogForm/components/RightColumn/components/transcriptUtils';

function parseKeywords(signalData: Record<string, unknown>) {
  return Array.isArray(signalData?.keywords) ? signalData.keywords : [];
}

function parseWorkflows(signalData: Record<string, unknown>) {
  return Array.isArray(signalData?.workflows) ? signalData.workflows : [];
}

function parseTaskCompleted(signalData: Record<string, unknown>) {
  const tasks = signalData?.tasks_completed;
  if (Array.isArray(tasks)) return tasks;
  if (tasks && typeof tasks === 'object') {
    return Object.entries(tasks as Record<string, number | boolean>)
      .filter(([, value]) => Boolean(value))
      .map(([key, value]) => {
        const label = titleCase(key.replace(/_/g, ' '));
        const count = typeof value === 'number' ? value : 1;
        return `${label} (${count})`;
      });
  }
  return [];
}

export default function CallLogForm({
  logId,
  open,
  onClose,
  showInteractionTypeFilter = false,
  businessScoped = false
}: {
  logId?: string;
  open?: boolean;
  onClose?: () => void;
  showInteractionTypeFilter?: boolean;
  businessScoped?: boolean;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: routeId } = useParams();
  const callLogId = logId ?? routeId;

  const [editingNote, setEditingNote] = useState<number | null>(null);
  const [selectedCallLogId, setSelectedCallLogId] = useState<string | undefined>(callLogId);
  const [stickyContactId, setStickyContactId] = useState<number | undefined>();

  const { data: businessProfile } = useGetBusinessProfile();
  const businessTimezone = businessProfile?.address?.time_zone;

  const { data: callLog, isLoading: isCallLogLoading } = useGetInteractionLog(
    selectedCallLogId,
    {
      include: 'contact,agent,business,intent_signal,email_logs,sms_logs'
    },
    { businessScoped }
  );
  const contactId = callLog?.contact_id ? Number(callLog.contact_id) : stickyContactId;

  useEffect(() => {
    if (callLog?.contact_id) {
      setStickyContactId(Number(callLog.contact_id));
    }
  }, [callLog?.contact_id]);

  const { data: associatedRecords, isLoading: isAssociatedRecordsLoading } = useGetAssociatedRecords(contactId);
  const { data: contact, isLoading: isContactLoading } = useGetContact(contactId!);
  const { data: notes } = useGetNotes(contactId!);
  const { mutateAsync: createNote, isPending: isCreatePending } = useCreateNote(contactId!);
  const { mutateAsync: updateNote, isPending: isUpdatePending } = useUpdateNote(contactId!);
  const { mutateAsync: deleteNote } = useDeleteNote(contactId!);

  const { handleSubmit, control, setValue, reset } = useForm<FormValues>({
    values: {
      contact: { phone_number: contact?.phone_number ?? null, call: callLog?.id || '' },
      note: { description: '' }
    }
  });

  useEffect(() => {
    if (!callLogId) return;
    setSelectedCallLogId(callLogId);
    setValue('contact.call', Number(callLogId));
  }, [callLogId, setValue]);

  const handleClose = () => {
    if (logId && onClose) onClose();
    else navigate({ pathname: '..', search: location.search }, { relative: 'path' });
  };

  const handleCreateOrUpdateNote = async (data: { note: { description: string } }) => {
    if (editingNote) {
      updateNote({ id: editingNote, payload: { note: data.note } });
      setEditingNote(null);
      reset();
    } else {
      await createNote({ note: data.note });
      reset();
    }
  };

  const handleEditClick = (note: { id: number; description: string }) => {
    setEditingNote(note.id);
    setValue('note.description', note.description);
  };

  const handleSelectCallLog = (id: number | undefined) => {
    setSelectedCallLogId(id != null ? String(id) : undefined);
  };

  const downloadTranscript = () => {
    const text = getVisibleJourneyItems(callLog?.interaction_journey)
      .map((item) => `${item.role === 'assistant' ? 'Agent' : 'Human'}: ${item?.content}`)
      .join('\n');
    const blob = new Blob([String(text)], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'transcript.txt';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const signalData = (callLog?.intent_signal ?? {}) as Record<string, unknown>;
  const formatLogDate = (date: string) => formatDateByTimezone(date, businessTimezone);
  const formatNoteDate = (date: string) => formatDateByTimezone(date, businessTimezone);
  const formatTranscriptTimestamp = (timestamp?: number) =>
    formatUnixByTimezone(timestamp, businessTimezone, 'h:mm:ss A');

  const formProps: Omit<InteractionLogFormProps, 'view'> = {
    isAgencyView: false,
    isOpen: logId ? !!open : !!routeId,
    onClose: handleClose,
    isLoading: Boolean(selectedCallLogId) && (isCallLogLoading || isContactLoading || isAssociatedRecordsLoading),
    callLog: callLog as InteractionLogFormProps['callLog'],
    callLogId: selectedCallLogId,
    contactId,
    contact: contact
      ? {
          phone_number: contact.phone_number,
          category: contact.category,
          chats_count: contact.chats_count,
          calls_count: contact.calls_count
        }
      : undefined,
    associatedRecords,
    notes,
    control,
    handleSubmit,
    setValue,
    editingNote,
    handleSelectCallLog,
    handleCreateOrUpdateNote,
    handleEditClick,
    deleteNote,
    downloadTranscript,
    isCreatePending,
    isUpdatePending,
    signalData: callLog?.intent_signal ?? {},
    endReason: callLog?.disconnection_reason ?? undefined,
    endReasonLabel: callLog?.disconnection_reason_label ?? undefined,
    keywords: parseKeywords(signalData),
    workflows: parseWorkflows(signalData),
    taskCompleted: parseTaskCompleted(signalData),
    systemSentiments: (signalData as { audit_issues?: { label: string; description: string }[] })?.audit_issues,
    agentName: '—',
    businessName: '—',
    formatLogDate,
    formatNoteDate,
    formatTranscriptTimestamp,
    showInteractionTypeFilter
  };

  return <InteractionLogForm view='business' {...formProps} />;
}
